import * as React from "react";
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  Field,
  tokens,
  makeStyles,
  Input,
  useRestoreFocusSource,
  useRestoreFocusTarget,
  Label,
  Body1,
  useId,
  mergeClasses,
  Dropdown,
  Option,
  Persona,
  Button,
  webLightTheme,
  FluentProvider,
  Combobox,
  Avatar,
  Badge,
  DrawerFooter,
  Textarea,
  Spinner,
} from "@fluentui/react-components";

import { DatePicker } from "@fluentui/react-datepicker-compat";
import { TimePicker } from "@fluentui/react-timepicker-compat";

import { Dismiss24Regular,PersonRegular, PeopleTeamFilled, LocationRippleFilled, AppTitleFilled, ContentViewRegular,CalendarCancelFilled } from "@fluentui/react-icons";

const useStyles = makeStyles({
    root: {
      // Stack the label above the field
      display: "flex",
      flexDirection: "column",
      // Use 2px gap below the label (per the design system)
      gap: "2px",
      // Prevent the example from taking the full width of the page (optional)
      //   maxWidth: "400px",
      padding:"10px"
    },
    container: {
        width: "500px",
        height: "300px",
        padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
        position: "relative",
        overflow: "hidden",
        border: `${tokens.strokeWidthThicker} solid ${tokens.colorNeutralStroke1}`,
      },

    field: {
      display: "grid",
      gridRowGap: tokens.spacingVerticalXXS,
      marginTop: tokens.spacingVerticalMNudge,
      padding: tokens.spacingHorizontalMNudge,
      marginBottom:"2px"
    },
    filledLighter: {
      backgroundColor: tokens.colorNeutralBackgroundInverted,
      "> label": {
        color: tokens.colorNeutralForegroundInverted2,
      },
    },
    popupFix: {
      // Global override for flyout popups from DatePicker and TimePicker
      ":global(.fui-Popover__surface)": {
        backgroundColor: tokens.colorNeutralBackground1, // or any solid background
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        boxShadow: tokens.shadow16,
      },
    },
});


export const CreateMeeting = ({open, onClose, setOpen, createMeeting, isLoading, setIsLoading, users, usersLoading},props)=> {
    const styles = useStyles();

    const dummyUsers = [
        {
            name:"Sahan Randika",
            email:"randikasahan652@gmail.com",
            status:"available",
            statusLabel:"Available"
        },
        {
            name:"Younus Mowlana",
            email:"younus@gmail.com",
            status:"away",
            statusLabel:"Away"
        }, {
            name:"Sandeepa Rathnayake",
            email:"sandeepa@gmail.com",
            status:"available",
            statusLabel:"Available"
        },
        {
            name:"Deshtha Thennakoon",
            email:"deshitha@gmail.com",
            status:"busy",
            statusLabel:"Busy"
        },
        {
            name:"Viraj M",
            email:"viraj@gmail.com",
            status:"out-of-office",
            statusLabel:"Out of Office"
        }
    ]

    const [customSize, setCustomSize] = React.useState(600);
    // const [users, setUsers] = React.useState(dummyUsers)
    const [activeOptionText, setActiveOptionText] = React.useState("");

    const [state, setState] = React.useState(
      {
        title:"",
        content: "",
        start_date:"",
        start_time:"",
        end_date: "",
        end_time: "",
        location: "Online",
        attendees:[]
      }
    )

    const idPrefix = "input-appearance-story";
    const inputIds = {
        default: useId(idPrefix),
        underline: useId(idPrefix),
        filledLighter: useId(idPrefix),
        filledDarker: useId(idPrefix),
        title: useId("title"),
        content: useId("content"),
        start_date: useId("start_date"),
        start_time: useId("start_time"),
        end_date: useId("end_date"),
        end_time: useId("end_time"),
        location: useId("location"),
        schedule_button: useId("schedule_button"),
        comboId: useId("combo-multi"),
        selectedListId: useId("combo-multi-selection"),
        dropdownId : useId("dropdown"),
    };
    
  
    // all Drawers need manual focus restoration attributes
    // unless (as in the case of some inline drawers, you do not want automatic focus restoration)
    const restoreFocusTargetAttributes = useRestoreFocusTarget();
    const restoreFocusSourceAttributes = useRestoreFocusSource();

    const onActiveOptionChange = React.useCallback((_, data) => {
        if (data?.nextOption?.text) {
          setActiveOptionText(data.nextOption.text);
        }
    }, [setActiveOptionText]);
      
    const onMouseEnter = React.useCallback((user) => {
        setActiveOptionText(user.name);
    }, [setActiveOptionText]);

    const handleChange = (ev, data) => {
      const name = ev.target.name; 
      const { value } = data;      
    
      setState((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handleDateChange = (field) => (date) => {
      setState(prev => ({
        ...prev,
        [field]: date,
      }));
    };

    const handleTimeChange = (field) => (event, data) => {
      setState(prev => ({
        ...prev,
        [field]: data.selectedTime,
      }));
    };

    const onSelect = (event, data) => {
      const selectedEmails = data.selectedOptions;
    
      // Map emails to full user objects from dummyUsers
      const selectedAttendees = dummyUsers.filter(user =>
        selectedEmails.includes(user.email)
      ).map(user => ({ name: user.name, email: user.email }));
    
      setState(prev => ({
        ...prev,
        attendees: selectedAttendees
      }));
    };
    
  
    const handleCreateMeeting = async()=>{
      setIsLoading("loading")
      
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; // e.g., "Asia/Colombo"

      const pad = (n) => String(n).padStart(2, '0');

      const formattedStart = `${state.start_time.getFullYear()}-${pad(state.start_time.getMonth() + 1)}-${pad(state.start_time.getDate())}T${pad(state.start_time.getHours())}:${pad(state.start_time.getMinutes())}:${pad(state.start_time.getSeconds())}`;

      const formattedEnd = `${state.end_time.getFullYear()}-${pad(state.end_time.getMonth() + 1)}-${pad(state.end_time.getDate())}T${pad(state.end_time.getHours())}:${pad(state.end_time.getMinutes())}:${pad(state.end_time.getSeconds())}`;


      const payload = {
        subject: state.title,
        body: {
          contentType: "HTML",
          content: state.content
        },
        start: {
          dateTime: formattedStart, // assumes ISO format input
          timeZone: timeZone
        },
        end: {
          dateTime: formattedEnd,
          timeZone: timeZone
        },
        location: {
          displayName: state.location || "Online"
        },
        attendees: state.attendees.map((attendee) => ({
          emailAddress: {
            address: attendee.email,
            name: attendee.name
          },
          type: "required"
        })),
        allowNewTimeProposals: true,
        isOnlineMeeting: true,
        onlineMeetingProvider: "teamsForBusiness"
      };
      
      await createMeeting(payload)

    }
    
  
    return (
      <div>
        <FluentProvider theme={webLightTheme}>
        <OverlayDrawer
          {...restoreFocusSourceAttributes}
          open={open}
          size="medium"
          position="end"
          onOpenChange={(_, state) => setOpen(open)}
          style={{backgroundColor:"Background"}}
        >
          <DrawerHeader>

            <DrawerHeaderTitle 
              heading={{ as: "h1" }} 
              style={{fontWeight:"bolder", paddingLeft:"15px", paddingTop:"5px", fontSize:"20px"}}
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  onClick={onClose}
                />
              }
            >Schedule New Meeting
            </DrawerHeaderTitle>

          </DrawerHeader>
  
          <DrawerBody>
            <div className={styles.root}>

                <div className={mergeClasses(styles.field)}>
                    <Label size="large" htmlFor={inputIds.filledLighter} style={{fontWeight:"bold"}}>Meeting Title</Label>
                    <Input 
                      style={{border:"1px solid", borderRadius:"15px"}} 
                      size="large" 
                      appearance="filled-lighter" 
                      id={inputIds.title} 
                      name="title"
                      contentBefore={<AppTitleFilled />} 
                      onChange={handleChange}
                      value={state.title}
                      type="text"
                    />
                </div>

                <div className={mergeClasses(styles.field)}>
                    <Label size="large" htmlFor={inputIds.filledLighter} style={{fontWeight:"bold"}}>Meeting description</Label>
                    <Textarea 
                      style={{border:"1px solid", borderRadius:"15px"}} 
                      size="large" 
                      appearance="filled-lighter" 
                      id={inputIds.content} 
                      name="content"
                      onChange={handleChange}
                      value={state.content}
                      type="text"
                    />
                </div>

                <div className={mergeClasses(styles.field)}>

                    <Label size="large"  htmlFor={inputIds.filledLighter} style={{fontWeight:"bold"}}>Start DateTime</Label>
                    <DatePicker
                        placeholder="Select..."
                        style={{backgroundColor:"Background",marginBottom:'5px',border:"1px solid", borderRadius:"10px"}}
                        id={inputIds.start_date}
                        value={state.start_date}
                        onSelectDate={handleDateChange("start_date")}
                        name="start_date"
                    />
                    <TimePicker 
                        placeholder="Select..."
                        style={{backgroundColor:"Background",border:"1px solid", borderRadius:"10px"}}
                        size="small"
                        id={inputIds.start_time}
                        value={state.start_time}
                        onTimeChange={handleTimeChange('start_time')}
                        name="start_time"
                        dateAnchor={state.start_date}
                    />
                    
                        
                </div>

                <div className={mergeClasses(styles.field)}>
                    <Label size="large" htmlFor={inputIds.filledLighter} style={{fontWeight:"bold"}}>End Date and Time</Label>
                    <DatePicker
                        placeholder="Select..."
                        style={{backgroundColor:"Background",marginBottom:'5px',border:"1px solid", borderRadius:"10px"}}
                        id={inputIds.end_date}
                        value={state.end_date}
                        onSelectDate={handleDateChange("end_date")}
                        name="end_date"
                    />
                    <TimePicker 
                        placeholder="Select..."
                        style={{backgroundColor:"Background",border:"1px solid", borderRadius:"10px"}}
                        size="small"
                        id={inputIds.end_time}
                        value={state.end_time}
                        onTimeChange={handleTimeChange('end_time')}
                        name="end_time"
                        dateAnchor={state.end_date}
                    />
                </div>

                <div className={mergeClasses(styles.field)}>
                    <Label size="large" htmlFor={inputIds.filledLighter} style={{fontWeight:"bold"}}>Add Location</Label>
                    <Input 
                      style={{border:"1px solid", borderRadius:"15px"}} 
                      size="large" 
                      id={inputIds.location} 
                      contentBefore={<LocationRippleFilled />} 
                      value={state.location}
                      name="location"
                      type="text"
                      onChange={handleChange}
                      />
                      
                </div>

                {/* <div className={mergeClasses(styles.field)}>
                <label id={inputIds.comboId}>Add Required Attendees</label>
                  <Combobox
                    multiselect={true}
                    placeholder="Select require attendees"
                    onOptionSelect={onSelect}
                    value={state.attendees?.map(a => a.name)}
                    selectedOptions={state.attendees.map(a => a.email)}
                    // onBlur={state.attendees?.map(a => a.name).join(", ")}
                  >
                    {dummyUsers.map((user,index) => (
                      <Option key={index} text={user.name} value={user.email}>
                        <Persona
                          avatar={{ color: "colorful", "aria-hidden": true }}
                          name={user.name}
                          presence={{
                          status: user.status,
                          }}
                          secondaryText={user.statusLabel}
                        />
                      </Option>
                    ))}
                  </Combobox>
                  {state.attendees.length>0 ? (
                    <span id={inputIds.selectedListId} className={styles.description}>
                      Selected:
                      {state.attendees.map((a, index) => (
                        <Badge
                          key={index}
                          appearance="filled"
                          color="brand"
                          icon={<PersonRegular/>}
                        >
                            {a.email}

                        </Badge>
                      ))}
                    </span>
                  ) : null}
               </div> */}

            </div>
          </DrawerBody>

          <DrawerFooter style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>

                  
                <Button 
                  appearance="primary" 
                  icon={isLoading==="loading"?<Spinner size="tiny" />:<PeopleTeamFilled/>}
                  onClick={handleCreateMeeting}
                  iconPosition="before"
                  id={inputIds.schedule_button}
                  disabledFocusable={isLoading !== "initial"}
                >
                  {isLoading === "loading"?
                    "Please Wait..."
                    :
                    "Schedule Meeting"
                  }
                  
                </Button>

                <Button 
                  appearance="secondary"
                  icon={<CalendarCancelFilled />}
                  onClick={onClose}
                  iconPosition="before"
                  id={inputIds.schedule_button}
                >
                  Cancel
                </Button>

          </DrawerFooter>
        </OverlayDrawer>
        </FluentProvider>
      </div>
    );
}
